import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import utc from "dayjs/plugin/utc";

dayjs.extend(quarterOfYear);
dayjs.extend(utc);

export const isDateAfter = (
  date1: string | number | Date,
  date2: string | number | Date
): boolean => {
  return dayjs(date1).isAfter(date2);
};

export const hasCreditCardDateExpired = (
  month: number,
  year: number
): boolean => {
  const currentMonth = dayjs().month() + 1;
  const currentYear = dayjs().year();

  return year < currentYear || (year === currentYear && month < currentMonth);
};

export const formatCreditCardDate = (month: number, year: number): string =>
  `${month < 10 ? `0${month}` : month}/${year}`;

// date as 1/1/2021 (mm/dd/yyyy)
export const shortDate = (date: string | number | Date): string =>
  dayjs(date).format("MM/DD/YYYY");

// date as 2023-01-31 (yyyy-mm-dd)
export const isoDate = (date: string | number | Date): string =>
  dayjs(date).format("YYYY-MM-DD");

export const shortToIsoString = (date: string): string =>
  dayjs.utc(date, "YYYY-MM-DD").toISOString();

// date as January 1, 2021
export const longDate = (date: string | number | Date): string =>
  dayjs(date).format("MMMM D, YYYY");

export const longDateWithTime = (date: string | number | Date): string =>
  dayjs(date).format("MMMM D, YYYY - h:mm A");

// Date as April 2025
export const monthYear = (date: string | number | Date): string =>
  dayjs(date).format("MMMM YYYY");

// Date Time as Nov 1, 12:00 PM
export const shortDateTime = (date: string | number | Date): string =>
  dayjs(date).format("MMM D, h:mm A");

// Date Time as Nov 1 at 12:00pm
export const shortDataAtTime = (date: string | number | Date): string =>
  dayjs(date).format("D MMM [at] h:mma");

// Date Time as MM/DD/YYYY h:mm A
export const shortDateTimeNumeric = (date: string | number | Date): string =>
  dayjs(date).format("MM/DD/YYYY h:mm A");

// Day is an integer, month is an full ISO string
export const dateFromDayIntAndMonthIso = ({
  day,
  month,
}: {
  day: number;
  month: string;
}): string => dayjs(month).date(day).format("YYYY-MM-DD");

export const getDateInXDays = (days: number | string): Date =>
  dayjs().add(parseInt(days.toString()), "day").toDate();

export const formatUnixTimestampToLongDate = (timestamp: number): string =>
  dayjs.unix(timestamp).format("MMMM D, YYYY");

export type DayNames = Array<
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
>;

export const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as DayNames;

export const disableSpecificDays = ({
  daysToDisable,
  date,
}: {
  daysToDisable?: DayNames;
  date: string | number | Date;
}): boolean => {
  if (!daysToDisable) {
    return false;
  }

  const day = dayjs(date).day();
  return daysToDisable.includes(daysOfWeek[day]);
};

export const getNumberSuffix = (num: number): string => {
  if (num % 10 === 1 && num % 100 !== 11) return "st";
  if (num % 10 === 2 && num % 100 !== 12) return "nd";
  if (num % 10 === 3 && num % 100 !== 13) return "rd";
  return "th";
};

export const getDateFromDayAndDate = ({
  day,
  date,
}: {
  day: number;
  date: string;
}): string => dayjs(date, "YYYY-MM-DD").date(day).format("YYYY-MM-DD");

export const getDateRangeSettings = () => ({
  "month-to-date": {
    startDate: dayjs().startOf("month").toISOString(),
    endDate: dayjs().toISOString(),
  },
  "quarter-to-date": {
    startDate: dayjs().startOf("quarter").toISOString(),
    endDate: dayjs().toISOString(),
  },
  "year-to-date": {
    startDate: dayjs().startOf("year").toISOString(),
    endDate: dayjs().toISOString(),
  },
  "last-month": {
    startDate: dayjs().subtract(1, "month").startOf("month").toISOString(),
    endDate: dayjs().subtract(1, "month").endOf("month").toISOString(),
  },
  "last-quarter": {
    startDate: dayjs().subtract(1, "quarter").startOf("quarter").toISOString(),
    endDate: dayjs().subtract(1, "quarter").endOf("quarter").toISOString(),
  },
  "last-year": {
    startDate: dayjs().subtract(1, "year").startOf("year").toISOString(),
    endDate: dayjs().subtract(1, "year").endOf("year").toISOString(),
  },
});

export type DateRangeKey = keyof ReturnType<typeof getDateRangeSettings>;

/**
 * Determines when the last payment date should be, based off the first payment date, the payment interval (weekly, monthly), and the lease start/end dates.
 * @param params - Object containing firstPaymentDate, paymentInterval, and leaseEndDate.
 * @returns The date of the last payment.
 */
export const getLastPaymentDate = ({
  firstPaymentDate,
  paymentInterval,
  leaseEndDate,
}: {
  firstPaymentDate: string | number | Date;
  paymentInterval: "weekly" | "monthly";
  leaseEndDate: string | number | Date;
}): string => {
  const firstPayment = dayjs(firstPaymentDate);
  const end = dayjs(leaseEndDate);

  let lastPayment: dayjs.Dayjs;

  if (paymentInterval === "weekly") {
    const weeks = end.diff(firstPayment, "week");
    lastPayment = firstPayment.add(weeks, "week");
    if (lastPayment.isAfter(end)) {
      lastPayment = end;
    }
    return lastPayment.format("YYYY-MM-DD");
  } else if (paymentInterval === "monthly") {
    const months = end.diff(firstPayment, "month");
    lastPayment = firstPayment.add(months, "month");
    if (lastPayment.isAfter(end)) {
      lastPayment = end;
    }
    return lastPayment.format("YYYY-MM-DD");
  }

  return "";
};

// Get difference in days between two dates. If no dates are provided, it defaults to 0.
export const getDaysDifference = (
  date1: null | undefined | string | number | Date,
  date2: null | undefined | string | number | Date
): number => {
  if (!date1 || !date2) {
    return 0;
  }

  return dayjs(date1).diff(dayjs(date2), "day");
};
